import React, { useState } from "react";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Lock, Mail, Loader2 } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ops-command-center-87/artifacts/fkrzr67n_4446800E-690F-4C11-82C8-015EFBEC0CAD.png";

export default function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (!res.ok) toast.error(res.error || "Login failed");
    else toast.success("Welcome back");
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left panel - brand */}
      <div className="hidden lg:flex lg:w-1/2 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: "radial-gradient(circle at 25% 30%, white 1px, transparent 1px), radial-gradient(circle at 75% 70%, white 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }} />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="HM" className="h-12 w-12 rounded bg-white p-1" />
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-slate-400">Hardik Meditech</div>
              <div className="text-lg font-bold">OPS CONTROL</div>
            </div>
          </div>
          <div>
            <h1 className="text-4xl xl:text-5xl font-bold leading-tight">
              The control room<br />for your entire<br />operation.
            </h1>
            <p className="mt-6 text-slate-300 text-base max-w-md leading-relaxed">
              Real-time visibility across purchase orders, production, dispatch,
              tenders, R&D, sales, certificates and field operations — in one place.
            </p>
            <div className="mt-10 grid grid-cols-3 gap-6 max-w-md">
              {[
                { k: "18", l: "Modules" },
                { k: "12", l: "Roles" },
                { k: "AI", l: "Weekly Review" },
              ].map((x) => (
                <div key={x.l}>
                  <div className="text-2xl font-bold text-white">{x.k}</div>
                  <div className="text-xs text-slate-400 uppercase tracking-wider mt-1">{x.l}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="text-xs text-slate-500">
            Healthcare · Education · Defence
          </div>
        </div>
      </div>

      {/* Right panel - form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-slate-50">
        <Card className="w-full max-w-md border-slate-200 shadow-lg">
          <CardContent className="p-8">
            <div className="flex items-center gap-2 mb-6 lg:hidden">
              <img src={LOGO_URL} alt="HM" className="h-10 w-10" />
              <div className="text-sm font-bold text-slate-900">HARDIK MEDITECH</div>
            </div>
            <div className="mb-8">
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Sign in</div>
              <h2 className="text-2xl font-bold text-slate-900 mt-1">Welcome back</h2>
              <p className="text-sm text-slate-500 mt-2">
                Enter your credentials to access the operations console.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-slate-700">Email</Label>
                <div className="relative mt-1.5">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="pl-10"
                    required
                    data-testid="login-email-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="password" className="text-slate-700">Password</Label>
                <div className="relative mt-1.5">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="pl-10"
                    required
                    data-testid="login-password-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-slate-900 hover:bg-slate-800 h-11"
                disabled={loading}
                data-testid="login-submit-button"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Sign In
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
